import { Container } from 'pixi.js';
import type { LayoutAnimationData, LayoutRawDataWithAnimation } from './types';
import { LayoutAnimationPlayer } from './LayoutAnimationPlayer';
import { triggerParticleFX } from '../../particles/triggerParticleFX';
import { makeFullScreenFlash } from '../../../game/views/common/makeFullScreenFlash';
import type { SoundType } from '../../../app/SoundManager';
import { soundManager } from '../../../app/SoundManager';

function createDummyLayoutPlayer(): LayoutAnimationPlayer {
  return new LayoutAnimationPlayer(
    { root: new Container() },
    { blocks: [], id: '', name: '', playingSpeed: 0, totalDuration: 0 },
  );
}

export function createLayoutAnimationPlayer(
  layoutInstance: unknown,
  animationName: string,
  onEvent?: (eventName: string) => void,
): LayoutAnimationPlayer {
  const rawData = (layoutInstance as { constructor: { raw: LayoutRawDataWithAnimation } }).constructor.raw;

  if (!rawData.animations) {
    console.error(`Layout doesn't have any animation data`);
    return createDummyLayoutPlayer();
  }

  const animationData: LayoutAnimationData | null =
    rawData.animations.find((data) => data.name === animationName) ?? null;

  if (!animationData) {
    console.error(`Cannot find animation ${animationName} in layout`);
    return createDummyLayoutPlayer();
  }

  return new LayoutAnimationPlayer(layoutInstance as Record<string, object>, animationData, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ANIMEVENT_: onEvent ?? ((): void => {}),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    FXPART_: (name: string, container): void => {
      void triggerParticleFX(name, container);
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    FXFLASH: (name): void => {
      let duration = 120;
      let color = 0xffffff;
      let opacity = 0.5;

      try {
        const [durationStr, colorStr, opacityStr] = name.split('_');

        const durationParsed = parseInt(durationStr, 10);
        if (!Number.isNaN(durationParsed)) duration = durationParsed;

        const colorParsed = parseInt(colorStr, 16);
        if (!Number.isNaN(colorParsed)) color = colorParsed;

        const opacityParsed = parseInt(opacityStr, 10);
        if (!Number.isNaN(opacityParsed)) opacity = opacityParsed / 100;
      } catch (e) {
        console.warn(
          `FXFLASH incorrect values. Format should be FXFLASH_duration_color_opacity (eg FXFLASH_200_FF00FF_0.1`,
        );
      }

      makeFullScreenFlash(duration, color, opacity);
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    SFX_: (name: string): void => {
      soundManager.play(name as SoundType);
    },
  });
}

export async function playLayoutAnimation(
  layoutInstance: unknown,
  animationName: string,
  loop = false,
  onEvent?: (eventName: string) => void,
): Promise<void> {
  const player = createLayoutAnimationPlayer(layoutInstance, animationName, onEvent);

  const onCompletePromise = new Promise<void>((resolve) => {
    player.onComplete.connect(() => {
      resolve();
    });
  });

  player.play(loop);

  await onCompletePromise;
}
